import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';

const PrivacyPolicy = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" gutterBottom color="primary" fontWeight="bold">
              Privacy Policy
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Last Updated: January 12, 2026
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Introduction */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Welcome to ZP Asha. We are committed to protecting your personal information and your right to privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use
              our mobile application and web portal designed for ASHA workers in Maharashtra.
            </Typography>
            <Typography variant="body1" paragraph>
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy,
              please do not access the application.
            </Typography>
          </Box>

          {/* Information We Collect */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              2. Information We Collect
            </Typography>
            <Typography variant="h6" gutterBottom fontWeight="500" sx={{ mt: 2 }}>
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              We collect personal information that you provide to us, including:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Name, email address, and phone number
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Professional credentials and designation
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                District and Taluka information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Login credentials
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom fontWeight="500" sx={{ mt: 2 }}>
              2.2 Health Records
            </Typography>
            <Typography variant="body1" paragraph>
              Through the HBNC (Home Based Newborn Care) and HBYC (Home Based Young Child) forms, we collect:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Mother and child health information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Visit schedules and outcomes
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Medical observations and recommendations
              </Typography>
            </Box>

            <Typography variant="h6" gutterBottom fontWeight="500" sx={{ mt: 2 }}>
              2.3 Technical Data
            </Typography>
            <Typography variant="body1" paragraph>
              We automatically collect certain information when you use our application:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Device information and operating system
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                IP address and location data
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Usage data and app interactions
              </Typography>
            </Box>
          </Box>

          {/* How We Use Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              3. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the information we collect to:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Facilitate healthcare service delivery to mothers and children
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Manage ASHA worker accounts and assignments
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Generate reports and analytics for health program monitoring
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Provide AI-powered chatbot assistance for ASHA workers
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Improve our services and user experience
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Ensure data security and prevent fraud
              </Typography>
            </Box>
          </Box>

          {/* Data Sharing */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              4. How We Share Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We may share your information with:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Government Health Departments:</strong> To support public health programs and policy-making
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Healthcare Providers:</strong> When necessary for patient care coordination
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Service Providers:</strong> Third-party vendors who assist in operating our platform (e.g., AWS for hosting)
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Legal Authorities:</strong> When required by law or to protect rights and safety
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              We do not sell your personal information to third parties.
            </Typography>
          </Box>

          {/* Data Security */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              5. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational security measures to protect your personal information:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Encrypted data transmission (HTTPS/SSL)
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Secure cloud storage with AWS
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Role-based access controls
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Regular security audits and updates
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </Typography>
          </Box>

          {/* Data Retention */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              6. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information for as long as necessary to:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                Fulfill the purposes outlined in this privacy policy
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Comply with legal and regulatory requirements
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                Support health program monitoring and evaluation
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              Health records are retained according to government healthcare data retention policies.
            </Typography>
          </Box>

          {/* Your Rights */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              7. Your Privacy Rights
            </Typography>
            <Typography variant="body1" paragraph>
              Depending on your location, you may have the following rights:
            </Typography>
            <Box component="ul" sx={{ pl: 4 }}>
              <Typography component="li" variant="body1" paragraph>
                <strong>Access:</strong> Request a copy of your personal information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Correction:</strong> Request correction of inaccurate information
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Deletion:</strong> Request deletion of your information (subject to legal requirements)
              </Typography>
              <Typography component="li" variant="body1" paragraph>
                <strong>Opt-out:</strong> Opt-out of certain data processing activities
              </Typography>
            </Box>
            <Typography variant="body1" paragraph>
              To exercise these rights, please contact us using the information below.
            </Typography>
          </Box>

          {/* Children's Privacy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              8. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our application collects health information about children as part of healthcare service delivery.
              This information is collected by authorized ASHA workers in the course of their professional duties
              and is protected under healthcare privacy regulations.
            </Typography>
          </Box>

          {/* Updates to Policy */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              9. Updates to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this privacy policy from time to time. The updated version will be indicated by an
              updated "Last Updated" date at the top of this policy. We encourage you to review this privacy
              policy periodically to stay informed about how we protect your information.
            </Typography>
          </Box>

          {/* Contact Information */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
              10. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions or concerns about this privacy policy or our data practices, please contact us:
            </Typography>
            <Box sx={{ pl: 2, bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>
              <Typography variant="body1" paragraph>
                <strong>Email:</strong> privacy@zpasha.gov.in
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Phone:</strong> +91-XXX-XXX-XXXX
              </Typography>
              <Typography variant="body1" paragraph>
                <strong>Address:</strong> Zilla Parishad Office<br />
                Maharashtra State Health Department<br />
                Maharashtra, India
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Footer Note */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" color="text.secondary">
              By using the ZP Asha application, you acknowledge that you have read and understood this Privacy Policy.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
